import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useCallback, useEffect, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { ArrowLeft } from 'lucide-react'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { FormErrorMessage } from '@/components/auth/FormErrorMessage'
import GoogleIcon from '@/icons/google'
import AppleIcon from '@/icons/apple'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useOnboardingStore } from '@/stores/onboarding'
// Assuming these functions will be updated to work with your auth provider
import { signInWithGoogle, signInWithApple, getOAuthRedirectResult } from '@/auth/firebase'
import { useGoogleAuthCallback, useAppleAuthCallback, useCheckEmailExists } from '@/hooks/api/useAuth'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'
import { captureException } from '@/config/sentry'
import { isWebPlatform } from '@/utils/platformDetection'

type RegisterSearchParams = {
    returnToPath?: string
    error?: string
}

export const Route = createFileRoute('/(onboarding)/(auth)/register/')({
    validateSearch: (
        search: Record<string, unknown>
    ): RegisterSearchParams => ({
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
        error:
            typeof search.error === 'string'
                ? search.error
                : undefined,
    }),
    component: RegisterRoute,
})

// Password validation schema
const passwordSchema = z
    .string()
    .min(6, 'Password must be at least 6 characters')

const registerSchema = z.object({
    fullName: z
        .string()
        .trim()
        .min(2, 'Full name must be at least 2 characters'),
    email: z.string().trim().email('Please enter a valid email address'),
    password: passwordSchema,
    acceptedTerms: z.boolean().refine((val) => val === true, {
        message: 'You must accept the Terms of Use to create an account',
    }),
})

type RegisterFormData = z.infer<typeof registerSchema>

function RegisterRoute() {
    const { returnToPath } = Route.useSearch()
    const navigate = useNavigate()
    const { registrationData, setRegistrationData, markStepCompleted } =
        useOnboardingStore()
    const [isLoading, setIsLoading] = useState(false)
    const [displayError, setDisplayError] = useState<string | null>(null)
    const [emailExists, setEmailExists] = useState(false)
    const posthog = usePostHog()
    // Track if redirect handler is handling navigation to prevent double navigation
    const isRedirectHandlerNavigating = useRef(false)

    // Track onboarding started when user lands on registration form
    useEffect(() => {
        posthog?.capture(AnalyticsEvents.ONBOARDING_STARTED)
    }, [posthog])

    // Replace with your Google auth implementation
    const {
        mutateAsync: googleAuthCallback,
        isPending: isGoogleCallbackLoading,
        reset: resetGoogleCallback,
    } = useGoogleAuthCallback()

    // Apple auth callback hook
    const {
        mutateAsync: appleAuthCallback,
        isPending: isAppleCallbackLoading,
        reset: resetAppleCallback,
    } = useAppleAuthCallback()

    // Redirect to username step if user already started with social auth (Google or Apple)
    // (handles edge cases like page refresh or manual URL navigation)
    // Skip navigation if redirect handler is already handling it to prevent double navigation
    const isSocialAuth = registrationData.authMethod === 'google' || registrationData.authMethod === 'apple'
    useEffect(() => {
        if (isSocialAuth && !isRedirectHandlerNavigating.current) {
            navigate({
                to: '/register/username',
                search: { returnToPath, error: undefined },
            })
        }
    }, [isSocialAuth, navigate, returnToPath])

    // Handle OAuth redirect result when user returns from redirect-based sign-in (Google or Apple)
    // Uses unified handler to avoid race condition where separate handlers would consume each other's credentials
    useEffect(() => {
        // Skip redirect result handling on native platforms - native auth is used instead
        // getRedirectResult() throws auth/argument-error on Capacitor iOS/Android
        if (!isWebPlatform()) {
            return
        }

        let isMounted = true

        const handleRedirectResult = async () => {
            try {
                const { credential, providerId, returnToPath: storedReturnToPath } =
                    await getOAuthRedirectResult()

                if (!credential || !providerId || !isMounted) {
                    return
                }

                setIsLoading(true)
                setDisplayError(null)

                // Determine which provider was used
                const isGoogle = providerId === 'google.com'

                const idToken = await credential.user.getIdToken()

                if (!idToken) {
                    throw new Error(
                        `Could not retrieve ID token from ${isGoogle ? 'Google' : 'Apple'} Sign-In.`
                    )
                }

                // Route to appropriate callback based on provider
                const callbackResponse = isGoogle
                    ? await googleAuthCallback({ idToken })
                    : await appleAuthCallback({ idToken })

                if (!isMounted) {
                    return
                }

                if (callbackResponse.status === 'logged_in') {
                    // Check for marker to handle cleanup
                    const markerStr = sessionStorage.getItem('authFlowMarker')
                    if (markerStr) {
                        try {
                            const marker = JSON.parse(markerStr) as {
                                historyLength: number
                                startPath: string
                            }
                            const stepsBack =
                                window.history.length - marker.historyLength
                            if (stepsBack > 0) {
                                window.history.go(-stepsBack)
                                return
                            }
                        } catch (e) {
                            console.error('Marker cleanup failed:', e)
                            sessionStorage.removeItem('authFlowMarker')
                        }
                    }
                    navigate({
                        to: storedReturnToPath || returnToPath || '/',
                    })
                } else if (callbackResponse.status === 'pending_registration') {
                    // Set flag to prevent guard effect from navigating
                    isRedirectHandlerNavigating.current = true
                    setRegistrationData({
                        authMethod: isGoogle ? 'google' : 'apple',
                        registrationToken: idToken,
                        email: credential.user.email || undefined,
                        fullName: credential.user.displayName || undefined,
                        avatarUrl: credential.user.photoURL || undefined,
                    })
                    // Navigate to username step (guard effect will skip due to flag)
                    navigate({
                        to: '/register/username',
                        search: {
                            returnToPath: storedReturnToPath || returnToPath,
                            error: undefined,
                        },
                    })
                    // Reset flag after guard effect has had time to run
                    // Use requestAnimationFrame to ensure React has processed effects
                    requestAnimationFrame(() => {
                        if (isMounted) {
                            isRedirectHandlerNavigating.current = false
                        }
                    })
                }
            } catch (error: any) {
                if (!isMounted) {
                    return
                }
                console.error('OAuth redirect result handling failed:', error)
                // In redirect flow, user cancellation is handled by credential being null (early return above)
                // Any error here is a real error that should be reported
                captureException(error, {
                    authMethod: 'oauth-redirect',
                    flow: 'registration',
                    source: 'redirect',
                })
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Sign in failed. Please try again.'
                setDisplayError(message)
            } finally {
                if (isMounted) {
                    setIsLoading(false)
                }
            }
        }

        handleRedirectResult()

        return () => {
            isMounted = false
            // Reset flag on cleanup to ensure proper state management across component lifecycle
            // This prevents stale flag state if component remounts before navigation completes
            isRedirectHandlerNavigating.current = false
        }
    }, [googleAuthCallback, appleAuthCallback, navigate, returnToPath, setRegistrationData])

    const { mutateAsync: checkEmailExists, isPending: isCheckingEmail } =
        useCheckEmailExists()

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        mode: 'onBlur',
        defaultValues: {
            fullName: registrationData.fullName || '',
            email: registrationData.email || '',
            password: registrationData.password || '',
            acceptedTerms: false,
        },
    })

    const handleBack = useCallback(() => {
        navigate({ to: '/onboarding', search: { returnToPath } })
    }, [navigate, returnToPath])

    const handleLogin = useCallback(() => {
        navigate({ to: '/login', search: { returnToPath } })
    }, [navigate, returnToPath])

    // Handle Google Sign-in
    const handleGoogleSignIn = async () => {
        console.log('[Google Sign-In] Handler started')
        setDisplayError(null)
        resetGoogleCallback()

        // Check if user has accepted terms
        const acceptedTerms = watch('acceptedTerms')
        console.log('[Google Sign-In] acceptedTerms value:', acceptedTerms)
        if (!acceptedTerms) {
            console.log('[Google Sign-In] Terms not accepted, showing error')
            setDisplayError('You must accept the Terms of Use to create an account')
            return
        }

        try {
            console.log('[Google Sign-In] Setting isLoading to true')
            setIsLoading(true)
            console.log('[Google Sign-In] Calling signInWithGoogle...')
            const userCredential = await signInWithGoogle(returnToPath)
            console.log('[Google Sign-In] signInWithGoogle returned:', userCredential ? 'credential' : 'null')

            // If null, user was redirected (in-app browser flow)
            // The redirect result will be handled by the useEffect hook
            if (!userCredential) {
                return
            }

            const idToken = await userCredential.user.getIdToken()

            if (!idToken) {
                throw new Error(
                    'Could not retrieve ID token from Google Sign-In.'
                )
            }

            const callbackResponse = await googleAuthCallback({ idToken })

            if (callbackResponse.status === 'logged_in') {
                // Check for marker to handle cleanup
                const markerStr = sessionStorage.getItem('authFlowMarker')
                if (markerStr) {
                    try {
                        const marker = JSON.parse(markerStr) as {
                            historyLength: number
                            startPath: string
                        }
                        const stepsBack =
                            window.history.length - marker.historyLength
                        if (stepsBack > 0) {
                            window.history.go(-stepsBack)
                            return
                        }
                    } catch (e) {
                        console.error('Marker cleanup failed:', e)
                        sessionStorage.removeItem('authFlowMarker')
                    }
                }
                navigate({ to: returnToPath || '/' })
            } else if (callbackResponse.status === 'pending_registration') {
                // Set flag to prevent guard effect from navigating (popup flow)
                isRedirectHandlerNavigating.current = true
                setRegistrationData({
                    authMethod: 'google',
                    registrationToken: idToken,
                    email: userCredential.user.email || undefined,
                    fullName: userCredential.user.displayName || undefined,
                    avatarUrl: userCredential.user.photoURL || undefined,
                })
                // Navigate to username step (guard effect will skip due to flag)
                navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
                // Reset flag after guard effect has had time to run
                requestAnimationFrame(() => {
                    isRedirectHandlerNavigating.current = false
                })
            }
        } catch (error: any) {
            console.error('Google sign in or callback failed:', error)
            // Don't show error for user cancellation
            const isCancelled =
                error.code === 'auth/popup-closed-by-user' ||
                error.message?.toLowerCase().includes('cancel')

            if (!isCancelled) {
                captureException(error, { authMethod: 'google', flow: 'registration' })
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Google sign in failed. Please try again.'
                setDisplayError(message)
            }
        } finally {
            setIsLoading(false)
        }
    }

    // Handle Apple Sign-in
    const handleAppleSignIn = async () => {
        console.log('[Apple Sign-In] Handler started')
        setDisplayError(null)
        resetAppleCallback()

        // Check if user has accepted terms
        const acceptedTerms = watch('acceptedTerms')
        console.log('[Apple Sign-In] acceptedTerms value:', acceptedTerms)
        if (!acceptedTerms) {
            console.log('[Apple Sign-In] Terms not accepted, showing error')
            setDisplayError('You must accept the Terms of Use to create an account')
            return
        }

        try {
            console.log('[Apple Sign-In] Setting isLoading to true')
            setIsLoading(true)
            console.log('[Apple Sign-In] Calling signInWithApple...')
            const userCredential = await signInWithApple(returnToPath)
            console.log('[Apple Sign-In] signInWithApple returned:', userCredential ? 'credential' : 'null')

            // If null, user cancelled or was redirected (redirect flow on web)
            // On Capacitor, this means user cancelled - the finally block will reset loading
            // On web redirect flow, the redirect result will be handled by the useEffect hook
            if (!userCredential) {
                console.log('[Apple Sign-In] No credential - returning early')
                return
            }

            console.log('[Apple Sign-In] Getting ID token from credential...')
            const idToken = await userCredential.user.getIdToken()
            console.log('[Apple Sign-In] ID token:', idToken ? 'received' : 'null')

            if (!idToken) {
                throw new Error(
                    'Could not retrieve ID token from Apple Sign-In.'
                )
            }

            console.log('[Apple Sign-In] Calling appleAuthCallback...')
            const callbackResponse = await appleAuthCallback({ idToken })
            console.log('[Apple Sign-In] Callback response:', JSON.stringify(callbackResponse))

            if (callbackResponse.status === 'logged_in') {
                console.log('[Apple Sign-In] User logged in, navigating...')
                // Check for marker to handle cleanup
                const markerStr = sessionStorage.getItem('authFlowMarker')
                if (markerStr) {
                    try {
                        const marker = JSON.parse(markerStr) as {
                            historyLength: number
                            startPath: string
                        }
                        const stepsBack =
                            window.history.length - marker.historyLength
                        if (stepsBack > 0) {
                            window.history.go(-stepsBack)
                            return
                        }
                    } catch (e) {
                        console.error('Marker cleanup failed:', e)
                        sessionStorage.removeItem('authFlowMarker')
                    }
                }
                navigate({ to: returnToPath || '/' })
            } else if (callbackResponse.status === 'pending_registration') {
                console.log('[Apple Sign-In] Pending registration, navigating to username step...')
                // Set flag to prevent guard effect from navigating (popup flow)
                isRedirectHandlerNavigating.current = true
                setRegistrationData({
                    authMethod: 'apple',
                    registrationToken: idToken,
                    email: userCredential.user.email || undefined,
                    fullName: userCredential.user.displayName || undefined,
                    avatarUrl: userCredential.user.photoURL || undefined,
                })
                // Navigate to username step (guard effect will skip due to flag)
                navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
                // Reset flag after guard effect has had time to run
                requestAnimationFrame(() => {
                    isRedirectHandlerNavigating.current = false
                })
            } else {
                console.log('[Apple Sign-In] Unexpected callback status:', callbackResponse.status)
            }
        } catch (error: any) {
            console.error('[Apple Sign-In] Error caught:', {
                message: error.message,
                code: error.code,
                name: error.name,
                stack: error.stack,
            })
            // Don't show error for user cancellation
            const isCancelled =
                error.code === 'auth/popup-closed-by-user' ||
                error.code === 1000 || // Apple cancellation error (number)
                error.code === 1001 || // Apple cancellation error (number)
                error.code === '1000' || // Apple cancellation error (string)
                error.code === '1001' || // Apple cancellation error (string)
                error.code === 'CANCELED' ||
                error.message?.toLowerCase().includes('cancel') ||
                error.message?.includes('AuthorizationError') || // Apple native error pattern
                error.message?.includes('error 1000') || // Apple error 1000 in message
                error.message?.includes('error 1001') // Apple error 1001 in message

            console.log('[Apple Sign-In] Is cancellation?', isCancelled)
            if (!isCancelled) {
                captureException(error, { authMethod: 'apple', flow: 'registration' })
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Apple sign in failed. Please try again.'
                setDisplayError(message)
            }
        } finally {
            console.log('[Apple Sign-In] Finally block - setting isLoading to false')
            setIsLoading(false)
        }
    }

    // Handle form submission
    const handleRegister = async (data: RegisterFormData) => {
        try {
            setIsLoading(true)
            setDisplayError(null)
            setEmailExists(false)

            // Check if email already exists
            const exists = await checkEmailExists(data.email)

            if (exists) {
                setEmailExists(true)
                setIsLoading(false)
                return
            }

            setRegistrationData({
                fullName: data.fullName,
                email: data.email,
                password: data.password,
                authMethod: 'email',
            })
            markStepCompleted('basic-info')
            navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
        } catch (error) {
            console.error('Form submission failed:', error)
            setDisplayError(
                'Failed to save registration details. Please try again.'
            )
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex justify-between px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />

                <GlossyButton
                    variant="ghost"
                    className="rounded-xl bg-white/25 px-4 py-2.5 backdrop-blur md:fixed md:right-2.5"
                    onClick={handleLogin}
                    disabled={isLoading}
                >
                    Log in
                </GlossyButton>
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Create your
                        <br className="md:hidden" />
                        <span className="text-white-48"> account</span>
                    </h1>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <form
                        onSubmit={handleSubmit(handleRegister)}
                        className="space-y-1"
                    >
                        <GlossyButton
                            variant="social"
                            className="w-full gap-2 rounded-xl bg-white/48 py-3 text-sm font-normal backdrop-blur"
                            glassEffect={{
                                opacity: 'bg-white/20',
                                blur: 'backdrop-blur-md',
                            }}
                            type="button"
                            onClick={handleGoogleSignIn}
                            disabled={isLoading || isCheckingEmail || isGoogleCallbackLoading || isAppleCallbackLoading}
                        >
                            <GoogleIcon />
                            {isGoogleCallbackLoading
                                ? 'Connecting...'
                                : 'Continue with Google'}
                        </GlossyButton>

                        <GlossyButton
                            variant="social"
                            className="w-full gap-2 rounded-xl bg-white/48 py-3 text-sm font-normal backdrop-blur"
                            glassEffect={{
                                opacity: 'bg-white/20',
                                blur: 'backdrop-blur-md',
                            }}
                            type="button"
                            onClick={handleAppleSignIn}
                            disabled={isLoading || isCheckingEmail || isGoogleCallbackLoading || isAppleCallbackLoading}
                        >
                            <AppleIcon />
                            {isAppleCallbackLoading
                                ? 'Connecting...'
                                : 'Continue with Apple'}
                        </GlossyButton>

                        {displayError && (
                            <FormErrorMessage
                                message={displayError}
                                center
                                className="mb-2"
                            />
                        )}
                        <p className="my-3 text-center text-sm leading-none font-normal text-white">
                            OR
                        </p>

                        <div>
                            <GlossyInput
                                {...register('fullName')}
                                placeholder="Full name"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                                focusedBorderColor={
                                    errors.fullName
                                        ? 'border-red-500'
                                        : 'border-white'
                                }
                                unfocusedBorderColor={
                                    errors.fullName
                                        ? 'border-red-500/70'
                                        : 'border-white/30'
                                }
                                disabled={isLoading || isCheckingEmail}
                            />
                            {errors.fullName && (
                                <FormErrorMessage
                                    message={errors.fullName.message || ''}
                                />
                            )}
                        </div>

                        <div>
                            <GlossyInput
                                {...register('email')}
                                type="email"
                                placeholder="Email address"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                                focusedBorderColor={
                                    errors.email || emailExists
                                        ? 'border-red-500'
                                        : 'border-white'
                                }
                                unfocusedBorderColor={
                                    errors.email || emailExists
                                        ? 'border-red-500/70'
                                        : 'border-white/30'
                                }
                                disabled={isLoading || isCheckingEmail}
                                onChange={(e) => {
                                    setEmailExists(false)
                                    register('email').onChange(e)
                                }}
                            />
                            {errors.email && (
                                <FormErrorMessage
                                    message={errors.email.message || ''}
                                />
                            )}
                            {emailExists && !errors.email && (
                                <div className="mt-1 text-sm text-white">
                                    This email is already registered.{' '}
                                    <button
                                        type="button"
                                        onClick={handleLogin}
                                        className="font-semibold underline"
                                    >
                                        Log in instead
                                    </button>
                                </div>
                            )}
                        </div>

                        <div>
                            <GlossyInput
                                {...register('password')}
                                type="password"
                                placeholder="Password"
                                glassEffect={{
                                    opacity: 'bg-white/20',
                                    blur: 'backdrop-blur-md',
                                }}
                                focusedBorderColor={
                                    errors.password
                                        ? 'border-red-500'
                                        : 'border-white'
                                }
                                unfocusedBorderColor={
                                    errors.password
                                        ? 'border-red-500/70'
                                        : 'border-white/30'
                                }
                                disabled={isLoading || isCheckingEmail}
                            />
                            {errors.password && (
                                <FormErrorMessage
                                    message={errors.password.message || ''}
                                />
                            )}
                        </div>

                        {/* Terms of Service Acceptance Checkbox - Required for Apple UGC Compliance */}
                        <div className="pt-3">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    {...register('acceptedTerms')}
                                    className="mt-1 h-5 w-5 rounded border-2 border-white/50 bg-transparent accent-white checked:bg-white focus:ring-2 focus:ring-white/30"
                                    disabled={isLoading || isCheckingEmail}
                                />
                                <span className="text-sm text-white leading-tight">
                                    I have read and agree to the{' '}
                                    <Link
                                        to="/terms-of-use"
                                        className="font-semibold underline hover:text-white/80"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Terms of Use
                                    </Link>{' '}
                                    and{' '}
                                    <Link
                                        to="/privacy-policy"
                                        className="font-semibold underline hover:text-white/80"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Privacy Policy
                                    </Link>
                                </span>
                            </label>
                            {errors.acceptedTerms && (
                                <FormErrorMessage
                                    message={errors.acceptedTerms.message || ''}
                                />
                            )}
                        </div>
                    </form>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="flex-none rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                            disabled={isLoading || isCheckingEmail}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[13px] text-sm"
                            type="submit"
                            onClick={handleSubmit(handleRegister)}
                            disabled={isLoading || isCheckingEmail}
                        >
                            {isLoading || isCheckingEmail
                                ? 'Please wait...'
                                : 'Continue'}
                        </GlossyButton>
                    </div>

                </FadeInUp>
            </div>
        </section>
    )
}
